import { decode } from './hlc.js';
import { emptyManifest, fieldValue, isArchived, isDeleted, projectEvent, resolveTagId, } from './merge.js';
import { rankOf, sumByCurrency } from './money.js';
import { releasedAfter } from './release.js';
import { daysBetween, lastReviewedAt, today } from './review.js';
import { buildTree, flattenTree, withDescendants } from './taxonomy.js';
/** Items that count towards a report: owned, not deleted, not released. */
export function reportableItems(manifest) {
    return [...manifest.items.values()].filter((item) => !isDeleted(item) && !isArchived(item));
}
const priceOf = (item) => fieldValue(item, 'purchasePrice') ?? null;
/**
 * Quantity is not folded into the count.
 *
 * "Six forks" is one item in the catalog and one decision to make about them,
 * which is the unit every other screen uses. Counting it as six here would make
 * the reports disagree with the list they link to.
 */
const rowFor = (groupId, label, items, nested = items) => ({
    groupId,
    label,
    count: items.length,
    value: sumByCurrency(items.map(priceOf)),
    itemIds: items.map((item) => item.itemId),
    nestedCount: nested.length,
    nestedValue: sumByCurrency(nested.map(priceOf)),
});
/** Biggest first by value, then by count, then by name: a stable, readable order. */
const ranked = (rows) => rows.sort((a, b) => rankOf(b.nestedValue) - rankOf(a.nestedValue) ||
    b.nestedCount - a.nestedCount ||
    a.label.localeCompare(b.label));
// ---------------------------------------------------------------------------
// FR-9.1 — counts and value by category and by location
// ---------------------------------------------------------------------------
/**
 * Grouped by a taxonomy, with both a direct and a nested figure.
 *
 * Both, because either alone misleads. A parent category whose items all sit in
 * its children would read as empty on the direct figure; a nested figure alone
 * would make a total look like it belongs to the parent itself and double-count
 * when the rows are read as a list.
 */
function byTaxonomy(manifest, which, field, noneLabel) {
    const items = reportableItems(manifest);
    const tree = buildTree(which === 'categories' ? manifest.categories : manifest.locations);
    const nodes = flattenTree(tree);
    const direct = new Map();
    const unassigned = [];
    for (const item of items) {
        const id = fieldValue(item, field);
        // An id that no longer resolves counts as unassigned rather than as its own
        // row: deleting a category leaves its items pointing at nothing (FR-4.7),
        // and a row labeled with a bare uuid would be no help to anyone.
        if (typeof id !== 'string' || id === '' || !nodes.some((node) => node.id === id)) {
            unassigned.push(item);
            continue;
        }
        const bucket = direct.get(id);
        if (bucket === undefined)
            direct.set(id, [item]);
        else
            bucket.push(item);
    }
    const rows = nodes.map((node) => {
        const family = withDescendants(tree, node.id);
        const nested = items.filter((item) => {
            const id = fieldValue(item, field);
            return typeof id === 'string' && family.has(id);
        });
        return rowFor(node.id, node.name, direct.get(node.id) ?? [], nested);
    });
    // The unassigned row sits last whatever its size: it is a gap in the record
    // rather than a group of things, and ranking it first would read as a finding.
    return [...ranked(rows), ...(unassigned.length > 0 ? [rowFor(null, noneLabel, unassigned)] : [])];
}
export const byCategory = (manifest) => byTaxonomy(manifest, 'categories', 'categoryId', 'Uncategorized');
export const byLocation = (manifest) => byTaxonomy(manifest, 'locations', 'locationId', 'No location');
// ---------------------------------------------------------------------------
// FR-9.8 — counts and value by tag
// ---------------------------------------------------------------------------
/**
 * Grouped by tag, with merged tags folded into their survivor.
 *
 * Untagged items are not given a row. Tags are optional by design and most items
 * carry none, so an "untagged" row would dominate every tag report and say
 * nothing -- unlike an uncategorized item, which is a gap worth seeing.
 *
 * An item carrying several tags is counted under each. The rows therefore do not
 * sum to the catalog size, which is a property of tags rather than a fault.
 */
export function byTag(manifest) {
    const grouped = new Map();
    for (const item of reportableItems(manifest)) {
        const seen = new Set();
        for (const [tagId, applied] of item.tags) {
            if (!applied.present)
                continue;
            const resolved = resolveTagId(manifest, tagId);
            // A merge can leave two applied ids resolving to one surviving tag, which
            // would otherwise count the item twice under its own tag.
            if (seen.has(resolved))
                continue;
            seen.add(resolved);
            const bucket = grouped.get(resolved);
            if (bucket === undefined)
                grouped.set(resolved, [item]);
            else
                bucket.push(item);
        }
    }
    const rows = [];
    for (const [tagId, items] of grouped) {
        const tag = manifest.tags.get(tagId);
        if (tag === undefined || tag.lifecycle?.present === false)
            continue;
        rows.push(rowFor(tagId, tag.displayName?.v ?? '(unnamed)', items));
    }
    return ranked(rows);
}
/**
 * How the catalog is distributed across verdicts.
 *
 * Reported in a fixed order with no total judgment attached. A screen that
 * ranked these, or called one of them a problem, would be recommending a
 * verdict, which NFR-SAFE-2 rules out -- the proportions are the finding.
 */
export function intentionSpread(manifest) {
    const items = reportableItems(manifest);
    const order = [
        'keep',
        'uncertain',
        'release',
        'invest',
        'unreviewed',
    ];
    const rows = order.map((intention) => {
        const matching = items.filter((item) => (item.currentIntention ?? 'unreviewed') === intention);
        return {
            intention,
            count: matching.length,
            share: items.length === 0 ? 0 : matching.length / items.length,
            value: sumByCurrency(matching.map(priceOf)),
            itemIds: matching.map((item) => item.itemId),
        };
    });
    return { rows, total: items.length };
}
export function reviewCoverage(manifest, withinDays = 365, asOf = today()) {
    const items = reportableItems(manifest);
    let reviewed = 0;
    let neverReviewed = 0;
    for (const item of items) {
        const at = lastReviewedAt(item);
        if (at === null) {
            neverReviewed += 1;
            continue;
        }
        if (daysBetween(new Date(at).toISOString().slice(0, 10), asOf) <= withinDays)
            reviewed += 1;
    }
    return {
        reviewed,
        total: items.length,
        // An empty catalog is not fully reviewed. Reporting 100% for nothing is
        // the kind of figure that quietly erodes trust in every other one.
        share: items.length === 0 ? 0 : reviewed / items.length,
        withinDays,
        neverReviewed,
    };
}
// ---------------------------------------------------------------------------
// FR-9.10 — the manifest as it stood on a past date
// ---------------------------------------------------------------------------
/**
 * Replays the log up to the end of a calendar date.
 *
 * This is the requirement the functional spec calls "the single most
 * consequential item in this revision", and it is answerable only because every
 * change is an append-only event: an item deleted outright, a tag renamed in
 * place or a category reassigned would each have destroyed the past. Here the
 * past is simply a shorter prefix of the same log through the same merge, which
 * is why no separate history store exists to drift from the live one.
 *
 * The cut is made on the event's hybrid logical clock rather than its
 * `clientTs`. The HLC is the ordering authority (ADR-0003): a device with a
 * skewed wall clock still produces events that sort correctly, and cutting on
 * the timestamp it reported would include or drop events inconsistently with
 * every other view of the same log.
 */
function cutoffOf(asAt) {
    const cutoff = Date.parse(`${asAt}T23:59:59.999Z`);
    if (Number.isNaN(cutoff))
        throw new Error(`not a calendar date: ${asAt}`);
    return cutoff;
}
export function manifestAsAt(events, asAt) {
    const cutoff = cutoffOf(asAt);
    const manifest = emptyManifest();
    for (const event of events) {
        let millis;
        try {
            millis = decode(event.hlc).millis;
        }
        catch {
            // An unreadable clock cannot be placed in time. Including it would put it
            // in every past manifest, which is the more wrong of the two answers.
            continue;
        }
        if (millis > cutoff)
            continue;
        projectEvent(manifest, event);
    }
    return manifest;
}
/**
 * FR-9.10's named figures: what was owned, and how it was filed, on a date --
 * and what has happened since (FR-9.11a).
 *
 * `current` is passed in rather than folded from the log again. It is the
 * manifest the rest of the app is reading, so the "now" figure here cannot
 * disagree with the catalog the user can go and count, which it could if this
 * built its own.
 *
 * The two "since" figures are flows and the two totals are stocks, and they are
 * not expected to reconcile: an item catalogued and then released inside the
 * same period appears in both, and a deletion appears in neither. The screen
 * says so rather than leaving someone to wonder why the arithmetic does not
 * close.
 */
export function compositionAsAt(events, asAt, current) {
    const manifest = manifestAsAt(events, asAt);
    const items = reportableItems(manifest);
    const cutoff = cutoffOf(asAt);
    return {
        asAt,
        items: items.length,
        value: sumByCurrency(items.map(priceOf)),
        byCategory: byCategory(manifest),
        byTag: byTag(manifest),
        itemsNow: reportableItems(current).length,
        catalogedSince: catalogedAfter(current, cutoff),
        releasedSince: releasedAfter(current, asAt).length,
    };
}
/**
 * Items first recorded after the cutoff.
 *
 * Counted on the creation clock rather than on an acquisition date: the
 * question is when it entered the catalog, not when it entered the house, and
 * an item bought years ago and catalogued last week was catalogued last week.
 *
 * Deleted items are left out, matching every other count here. A deletion is
 * someone saying the record should not exist, and "you catalogued 10 things"
 * where two of them were mistakes already undone is the less true answer.
 */
function catalogedAfter(manifest, cutoff) {
    let count = 0;
    for (const item of manifest.items.values()) {
        if (isDeleted(item))
            continue;
        if (item.createdHlc === null)
            continue;
        try {
            if (decode(item.createdHlc).millis > cutoff)
                count += 1;
        }
        catch {
            // Unplaceable in time, as in `manifestAsAt`. Not counted rather than
            // counted into every period.
            continue;
        }
    }
    return count;
}
//# sourceMappingURL=reports.js.map